import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CONTACT } from "../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#F9F8F6]/30 focus:border-gold rounded-none px-0 py-3.5 text-base md:text-sm font-normal text-[#F9F8F6] placeholder:text-[#F9F8F6]/50 outline-none transition-colors duration-300";

export default function LeadForm({ project, config = "", intent = "Enquiry", compact = false, onSuccess, idPrefix = "section" }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", config, website: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const projectName = project ? project.name : "Sobha One World";
  const tid = (base) => `${idPrefix}-${base}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        project: `${projectName} · ${intent}`,
        budget: form.config || "Not specified",
        website: form.website,
      });
      setDone(true);
      toast.success("Thank you — an advisor will reach out shortly.");
      if (onSuccess) onSuccess();
    } catch {
      toast.error("Something went wrong. Please call us at " + CONTACT.phone);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid={tid("lead-form-success")} className="text-center py-8">
        <CheckCircle2 size={44} className="text-gold mx-auto" strokeWidth={1.25} />
        <h3 className="font-serif-display text-2xl text-[#F9F8F6] mt-5">Request received.</h3>
        <p className="mt-3 text-sm font-normal text-[#F9F8F6]/70 leading-relaxed max-w-sm mx-auto">
          An advisor will call you shortly with the {intent.toLowerCase()} for {projectName}.
          Need it sooner?
        </p>
        <a
          data-testid={tid("lead-whatsapp")}
          href={CONTACT.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-6 bg-gold text-ink text-[12px] font-semibold uppercase tracking-[0.2em] px-8 py-4 hover:bg-[#F9F8F6] transition-colors"
        >
          Chat on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form data-testid={tid("lead-form")} onSubmit={submit}>
      <div className={compact ? "space-y-1" : "grid sm:grid-cols-2 gap-x-8 gap-y-2"}>
        <div>
          <label htmlFor={`${idPrefix}-lf-name`} className="text-[10px] uppercase tracking-[0.2em] text-[#F9F8F6]/50">Full name *</label>
          <input id={`${idPrefix}-lf-name`} data-testid={tid("lead-name-input")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={inputCls} autoComplete="name" />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-lf-phone`} className="text-[10px] uppercase tracking-[0.2em] text-[#F9F8F6]/50">Phone (WhatsApp) *</label>
          <input id={`${idPrefix}-lf-phone`} data-testid={tid("lead-phone-input")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91" className={inputCls} autoComplete="tel" inputMode="tel" />
        </div>
        <div className={compact ? "" : "sm:col-span-2"}>
          <label htmlFor={`${idPrefix}-lf-email`} className="text-[10px] uppercase tracking-[0.2em] text-[#F9F8F6]/50">Email (optional)</label>
          <input id={`${idPrefix}-lf-email`} data-testid={tid("lead-email-input")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className={inputCls} autoComplete="email" inputMode="email" />
        </div>
        {project && (
          <div className={`pt-3 ${compact ? "" : "sm:col-span-2"}`}>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#F9F8F6]/50 block mb-1">Preferred configuration</label>
            <Select value={form.config} onValueChange={(v) => setForm({ ...form, config: v })}>
              <SelectTrigger data-testid={tid("lead-config-select")} className="bg-transparent border-0 border-b border-[#F9F8F6]/30 rounded-none px-0 py-3.5 h-auto text-base md:text-sm font-normal text-[#F9F8F6] focus:ring-0 focus:border-gold">
                <SelectValue placeholder="Choose configuration" />
              </SelectTrigger>
              <SelectContent className="bg-ink border-[#F9F8F6]/15 text-[#F9F8F6] rounded-none max-h-72">
                {project.configs.map((c) => (
                  <SelectItem key={c} value={c} data-testid={tid(`lead-config-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`)} className="text-sm font-normal rounded-none">
                    {c}
                  </SelectItem>
                ))}
                <SelectItem value="Not sure yet" data-testid={tid("lead-config-not-sure")} className="text-sm font-normal rounded-none">
                  Not sure yet — advise me
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" aria-hidden="true" data-testid={tid("lead-honeypot")} />

      <button
        data-testid={tid("lead-submit-button")}
        type="submit"
        disabled={loading}
        className="mt-8 w-full bg-gold text-ink text-[12px] md:text-[13px] font-semibold uppercase tracking-[0.22em] py-4 hover:bg-[#F9F8F6] disabled:opacity-60 transition-colors duration-300 flex items-center justify-center gap-3 min-h-[52px]"
      >
        {loading && <Loader2 size={16} className="animate-spin" />}
        {loading ? "Sending…" : intent}
      </button>
      <p className="mt-4 text-[11px] font-normal text-[#F9F8F6]/50 leading-relaxed text-center">
        100% free advisory · Zero brokerage · We never share your details
      </p>
    </form>
  );
}
